<template>
    <article class="article">
        <AppBreadcrumbs
            :base-url="runtimeConfig.public.BASE_URL"
            :parents="[
                {
                    name: config.brand_name,
                    full_slug: '',
                },
                ...parentPages,
            ]"
            :current="{
                name: article.name,
                full_slug: article.full_slug,
            }"
        />
        <div class="article__main">
            <div class="article__content">
                <!-- The restaurant page's LCP image; eager + fetchpriority + the
                    preload prop ship together (see hero.vue). The CSS below owns
                    the visible crop (64vw mobile / 360px desktop), so the
                    transform ratio splits the difference and `cover` absorbs it. -->
                <NuxtImg
                    :src="article.content.image.filename"
                    :alt="article.content.image.alt"
                    width="1000"
                    height="640"
                    fit="cover"
                    format="auto"
                    sizes="xs:100vw sm:100vw md:768px"
                    loading="eager"
                    fetchpriority="high"
                    :preload="{ fetchPriority: 'high' }"
                    class="article__banner"
                />
                <h1
                    class="article__title"
                    v-html="renderHighlightedText(article.content.title)"
                />

                <div class="restaurant-info">
                    <div class="restaurant-info-item">
                        <Icon name="tabler:coin-euro" /> €{{ article.content.price_range_low }} - €{{ article.content.price_range_high }}
                    </div>
                    <div class="restaurant-info-item">
                        <Icon name="tabler:bowl-spoon" /> {{ article.content.food_type }}
                    </div>
                </div>

                <div
                    v-editable="article.content.text"
                    class="article__text"
                    v-html="bodyText"
                />

                <table class="restaurants-table">
                    <tr
                        v-for="row in tableData"
                        :key="row.title"
                    >
                        <td>{{ row.title }}</td>
                        <td>{{ row.value }}</td>
                    </tr>
                </table>
            </div>
            <div class="article__sidebar">
                <iframe
                    class="article__map"
                    loading="lazy"
                    frameborder="none"
                    allowfullscreen
                    :src="`https://www.google.com/maps/embed/v1/place?key=AIzaSyBTvgCYtqP8oVovD1HDofkcJGq1RP-Jx4U&q=${article.content.map_location}`"
                />

                <div class="sidebar-info-wrapper">
                    <ul class="sidebar-info">
                        <li class="sidebar-info-item">
                            <h2 class="sidebar-info-item__title u-header-4">
                                Contact
                            </h2>
                            <ul class="sidebar-info-item__list">
                                <li
                                    v-if="article.content.telephone"
                                    class="sidebar-info-item__list-item"
                                >
                                    <a :href="`tel:${article.content.telephone}`">
                                        <Icon name="tabler:phone" /> {{ article.content.telephone }}
                                    </a>
                                </li>
                                <li
                                    v-if="article.content.email"
                                    class="sidebar-info-item__list-item"
                                >
                                    <a :href="`mailto:${article.content.email}`">
                                        <Icon name="tabler:mail" /> {{ article.content.email }}
                                    </a>
                                </li>
                                <li class="sidebar-info-item__list-item">
                                    <Icon name="tabler:map-pin" /> {{ article.content.address }}
                                </li>
                            </ul>
                        </li>
                    </ul>

                    <ul class="sidebar-info">
                        <li class="sidebar-info-item">
                            <h2 class="sidebar-info-item__title u-header-4">
                                Opening times
                            </h2>
                            <div
                                class="sidebar-info-item__text"
                                v-html="renderRichText(article.content.opening_times)"
                            />
                        </li>
                    </ul>

                    <ul class="sidebar-info">
                        <li class="sidebar-info-item">
                            <h2 class="sidebar-info-item__title u-header-4">
                                Visit them
                            </h2>

                            <div class="sidebar-info-item__buttons">
                                <KButton
                                    v-if="article.content.reservation_link?.url"
                                    as="a"
                                    :href="article.content.reservation_link.url"
                                    label="Book Table"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    @click="capture('affiliate_click', {
                                        partner: 'reservation',
                                        placement: 'restaurant-sidebar',
                                        restaurant: article.name,
                                        url: article.content.reservation_link.url,
                                    })"
                                />

                                <KButton
                                    as="a"
                                    :href="`https://www.google.com/maps/dir/?api=1&destination=${article.content.map_location}`"
                                    label="Directions"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    @click="capture('restaurant_directions_click', {
                                        restaurant: article.name,
                                    })"
                                />
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </article>
</template>

<script setup>
import { RichTextSchema } from '@storyblok/vue'
import { KButton } from '@vemedia/karibu'
import { computed } from 'vue'
import { tocHeadingResolver } from '#shared/articleHeadings'
import { buildBreadcrumbParents } from '~/helpers/breadcrumb-parents'
import renderHighlightedText from '~/helpers/renderHighlightedText'
import seoTitle from '~/helpers/seoTitle'

const { data } = defineProps({
    data: {
        type: Object,
        required: true,
    },
})

const { data: config } = await useConfig()
const { capture } = useTracking()

const route = useRoute()
const { slug } = route.params

const parentPages = computed(() => buildBreadcrumbParents(slug))

const { article } = data

// The restaurant body is a rich-text field (not an article-text blok); render it with the
// TOC heading schema so the server-stamped h2/h3 ids reach the DOM and the TOC anchors
// resolve.
const bodyText = computed(() => renderRichText(article.content.text, {
    schema: { nodes: { ...RichTextSchema.nodes, heading: tocHeadingResolver }, marks: RichTextSchema.marks },
}))

const runtimeConfig = useRuntimeConfig()

const title = seoTitle(article)

const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': renderRichText(article.content.title).replaceAll(/<[^>]*>?/g, ''),
    'image': [
        `${article.content.image.filename}/m/960x960`,
        `${article.content.image.filename}/m/1920x1080`,
        `${article.content.image.filename}/m/960x720`,
    ],
    'datePublished': article.first_published_at,
    'dateModified': article.published_at,
}

// Only ever opt *out*. `index, follow` is already the @nuxtjs/robots default, and setting
// it explicitly would override the site-wide noindex that preview/staging hosts get from
// server/plugins/site-indexable.js. `noindex, follow` and not `useRobotsRule(false)`:
// `false` resolves to the module's `robotsDisabledValue` (`noindex, nofollow`), whose
// nofollow also cuts the crawl path to any indexable children.
if (article.content.index === false)
    useRobotsRule('noindex, follow')

useHead({
    title,
    script: [
        {
            type: 'application/ld+json',
            textContent: JSON.stringify(structuredData),
        },
    ],
    meta: [
        {
            hid: 'description',
            name: 'description',
            content: article.content.meta_description,
        },
        {
            property: 'og:type',
            content: 'article',
        },
        {
            property: 'og:url',
            content: `${runtimeConfig.public.BASE_URL}/${article.full_slug}`,
        },
        {
            property: 'og:title',
            content: title,
        },
        {
            property: 'og:description',
            content: article.content.meta_description,
        },
        {
            property: 'og:image',
            content: `${article.content.image.filename}/m/1200x630`,
        },
        {
            hid: 'twitter:card',
            name: 'twitter:card',
            content: 'summary_large_image',
        },
        {
            hid: 'twitter:domain',
            name: 'twitter:domain',
            content: runtimeConfig.public.BASE_URL,
        },
        {
            hid: 'twitter:url',
            name: 'twitter:url',
            content: `${runtimeConfig.public.BASE_URL}/${article.full_slug}`,
        },
        {
            hid: 'twitter:title',
            name: 'twitter:title',
            content: title,
        },
        {
            hid: 'twitter:description',
            name: 'twitter:description',
            content: article.content.meta_description,
        },
        {
            hid: 'twitter:image',
            name: 'twitter:image',
            content: `${article.content.image.filename}/m/1200x630`,
        },
    ],
})

onMounted(() => {
    capture('restaurant_viewed', {
        restaurant: article.name,
        food_type: article.content.food_type,
        price_range: `${article.content.price_range_low}-${article.content.price_range_high}`,
    })
})

const tableData = [
    {
        title: 'Wheelchair Accessible',
        value: article.content.accessible ? 'Yes' : 'No',
    },
    {
        title: 'Vegetarian food',
        value: article.content.vegetarian_food ? 'Yes' : 'No',
    },
    // {
    //     title: 'Payment options',
    //     value: article.content.payment_options,
    // },
    // only add if string is not empty
    ...(article.content.payment_options
        ? [
                {
                    title: 'Payment options',
                    value: article.content.payment_options,
                },
            ]
        : []),
    {
        title: 'Child friendly',
        value: article.content.child_friendly ? 'Yes' : 'No',
    },
    {
        title: 'Groups',
        value: article.content.groups ? 'Yes' : 'No',
    },
]
</script>

<style lang="scss">
@use '@vemedia/bcn-components/scss/responsive' as *;

.article {
    &__banner {
        display: block;
        width: calc(100% + 32px) !important;
        height: 64vw;
        margin-inline: -16px;
        object-fit: cover;

        @include desktop {
            width: 100% !important;
            height: 360px !important;
            margin-inline: 0;
            border-radius: 10px;
            overflow: hidden;
        }
    }

    &__title {
        margin-block: 1.5rem;
    }

    &__map {
        border-radius: 10px;
        height: 360px !important;

        @include desktop {
            height: 360px !important;
        }
    }

    &__main {
        @include below-desktop {
            flex-direction: column;
        }
    }

    &__sidebar {
        @include below-desktop {
            display: flex !important;
            flex-direction: column;
            gap: 1.5rem;
            padding: 2rem;
        }
    }
}
.sidebar-info-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.sidebar-info {
    list-style-type: none;
}

.sidebar-info-item {
    &__title {
        width: fit-content;
        margin-bottom: 1rem;
        padding: 0 1rem 0.75rem 0;
        border-bottom: 1px solid #000000;
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        list-style-type: none;

        &-item {
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;
            font-size: 1rem;

            svg {
                height: 1.5rem;
                width: 1rem;
                font-size: 1rem;
                color: var(--primary-button-background);
            }

            a {
                display: flex;
                gap: 0.5rem;
                color: inherit;

                &:hover {
                    text-decoration: underline;
                }
            }
        }
    }

    &__text {
        b {
            font-weight: 600;
        }
    }

    &__buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;

        :nth-child(2) {
            background: none;
            border: 2px solid var(--primary-button-background);
            color: var(--primary-button-background);
        }

        .bcn-button {
            margin-top: 0 !important;
        }
    }
}

.restaurants-table {
    width: 100%;
    margin-top: 1.5rem;
    border-collapse: collapse;

    tr {
        border-bottom: 1px solid #dddddd;

        &:first-child {
            border-top: 1px solid #dddddd;
        }

        td {
            padding: 0.75rem 0;
            text-align: left;

            &:nth-child(1) {
                width: 212px;
                font-weight: 600;
                padding-right: 0.5rem;
            }
        }
    }
}

.restaurant-info {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;

    &-item {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        font-size: 1rem;
        font-weight: 500;

        svg {
            height: 1.5rem;
            width: 1.5rem;
            font-size: 1.5rem;
            color: var(--primary-button-background);
        }
    }
}
</style>
